import { useTranslation } from 'react-i18next';
import PublicContentPage, { PublicCtaLink } from '../components/layout/PublicContentPage';
import { buildArticleSchema } from '../lib/seo';

const GUIDE_PATH = '/accessibility-audit-software-for-agencies';
const GUIDE_DATE = '2026-05-21';

interface GuideSection {
  title: string;
  body: string;
  points?: string[];
}

interface GuideStep {
  title: string;
  body: string;
}

interface GuideAction {
  href: string;
  label: string;
  variant: 'primary' | 'secondary' | 'ghost';
}

export default function AgencyAuditSoftwareGuidePage() {
  const { t } = useTranslation('marketing');
  const softwareChecklist = t('pages.agencyAuditSoftwareGuide.softwareChecklist.items', {
    returnObjects: true,
  }) as string[];
  const workflowSteps = t('pages.agencyAuditSoftwareGuide.workflow.steps', { returnObjects: true }) as GuideStep[];
  const sections = t('pages.agencyAuditSoftwareGuide.sections', { returnObjects: true }) as GuideSection[];
  const audience = t('pages.agencyAuditSoftwareGuide.audience.items', { returnObjects: true }) as string[];
  const sidebarPoints = t('pages.agencyAuditSoftwareGuide.sidebar.points', { returnObjects: true }) as string[];
  const ctaActions = t('pages.agencyAuditSoftwareGuide.cta.actions', { returnObjects: true }) as GuideAction[];

  return (
    <PublicContentPage
      metaTitle={t('pages.agencyAuditSoftwareGuide.meta.title')}
      metaDescription={t('pages.agencyAuditSoftwareGuide.meta.description')}
      canonicalPath={GUIDE_PATH}
      eyebrow={t('pages.agencyAuditSoftwareGuide.hero.eyebrow')}
      title={t('pages.agencyAuditSoftwareGuide.hero.title')}
      intro={t('pages.agencyAuditSoftwareGuide.hero.intro')}
      metaType="article"
      structuredData={[
        buildArticleSchema({
          headline: t('pages.agencyAuditSoftwareGuide.hero.title'),
          description: t('pages.agencyAuditSoftwareGuide.meta.description'),
          path: GUIDE_PATH,
          datePublished: GUIDE_DATE,
          dateModified: GUIDE_DATE,
        }),
      ]}
      aside={(
        <>
          <section className="acc-public-page-card acc-public-page-card--sticky">
            <h2>{t('pages.agencyAuditSoftwareGuide.sidebar.title')}</h2>
            <p>{t('pages.agencyAuditSoftwareGuide.sidebar.body')}</p>
            <ul className="acc-public-page-list">
              {sidebarPoints.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
          </section>

          <section className="acc-public-page-card">
            <h2>{t('pages.agencyAuditSoftwareGuide.sidebar.noteTitle')}</h2>
            <p>{t('pages.agencyAuditSoftwareGuide.sidebar.noteBody')}</p>
          </section>
        </>
      )}
    >
      <section className="acc-public-page-card">
        <h2>{t('pages.agencyAuditSoftwareGuide.intro.title')}</h2>
        <p>{t('pages.agencyAuditSoftwareGuide.intro.body')}</p>
        <p>{t('pages.agencyAuditSoftwareGuide.intro.note')}</p>
      </section>

      <section className="acc-public-page-card">
        <h2>{t('pages.agencyAuditSoftwareGuide.why.title')}</h2>
        <p>{t('pages.agencyAuditSoftwareGuide.why.body')}</p>
        <ul className="acc-public-page-list">
          {(t('pages.agencyAuditSoftwareGuide.why.points', { returnObjects: true }) as string[]).map((point) => (
            <li key={point}>{point}</li>
          ))}
        </ul>
      </section>

      <section className="acc-public-page-card">
        <h2>{t('pages.agencyAuditSoftwareGuide.softwareChecklist.title')}</h2>
        <ul
          className="acc-public-page-list"
          aria-label={t('pages.agencyAuditSoftwareGuide.softwareChecklist.ariaLabel')}
        >
          {softwareChecklist.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </section>

      <section className="acc-public-page-section" aria-labelledby="agency-workflow-title">
        <div>
          <h2 id="agency-workflow-title">{t('pages.agencyAuditSoftwareGuide.workflow.title')}</h2>
          <p>{t('pages.agencyAuditSoftwareGuide.workflow.body')}</p>
        </div>

        <div className="acc-public-page-grid">
          {workflowSteps.map((step, index) => (
            <article key={step.title} className="acc-public-page-subcard">
              <h3>
                {t('pages.agencyAuditSoftwareGuide.workflow.stepLabel', { number: index + 1 })}
                {' '}
                {step.title}
              </h3>
              <p>{step.body}</p>
            </article>
          ))}
        </div>
      </section>

      {sections.map((section) => (
        <section key={section.title} className="acc-public-page-card">
          <h2>{section.title}</h2>
          <p>{section.body}</p>
          {section.points && section.points.length > 0 && (
            <ul className="acc-public-page-list">
              {section.points.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
          )}
        </section>
      ))}

      <section className="acc-public-page-card">
        <h2>{t('pages.agencyAuditSoftwareGuide.audience.title')}</h2>
        <p>{t('pages.agencyAuditSoftwareGuide.audience.body')}</p>
        <ul className="acc-public-page-list">
          {audience.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </section>

      <section className="acc-public-page-card">
        <h2>{t('pages.agencyAuditSoftwareGuide.disclaimer.title')}</h2>
        <p>{t('pages.agencyAuditSoftwareGuide.disclaimer.body')}</p>
      </section>

      <section className="acc-public-cta-band" aria-labelledby="agency-guide-cta-title">
        <div>
          <h2 id="agency-guide-cta-title">{t('pages.agencyAuditSoftwareGuide.cta.title')}</h2>
          <p>{t('pages.agencyAuditSoftwareGuide.cta.body')}</p>
        </div>
        <div className="acc-public-cta-actions">
          {ctaActions.map((action) => (
            <PublicCtaLink key={action.href} href={action.href} variant={action.variant}>
              {action.label}
            </PublicCtaLink>
          ))}
        </div>
      </section>
    </PublicContentPage>
  );
}
